import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useListContext } from 'react-admin';

export const extractNonAlwaysOnSources = (children) => {
    const sources = [];
    const walk = (nodes) => {
        React.Children.forEach(nodes, (child) => {
            if (!React.isValidElement(child)) return;
            const { source, alwaysOn, children: nestedChildren } = child.props || {};
            if (source && !alwaysOn) {
                sources.push(source);
            } else if (!source && nestedChildren) {
                walk(nestedChildren);
            }
        });
    };

    walk(children);

    return sources;
};

const MoreFiltersButton = ({ filterInputs }) => {
    const { filterValues, displayedFilters, setFilters } = useListContext();

    const nonAlwaysOnSources = useMemo(
        () => extractNonAlwaysOnSources(filterInputs),
        [filterInputs]
    );

    if (nonAlwaysOnSources.length === 0) {
        return null;
    }

    const isAllExpanded =
        nonAlwaysOnSources.length > 0 &&
        nonAlwaysOnSources.every((source) => Boolean(displayedFilters?.[source]));

    const handleToggle = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (isAllExpanded) {
            const nextDisplayedFilters = { ...displayedFilters };
            const nextFilterValues = { ...filterValues };

            nonAlwaysOnSources.forEach((source) => {
                delete nextDisplayedFilters[source];
                delete nextFilterValues[source];
            });

            setFilters(nextFilterValues, nextDisplayedFilters);
        } else {
            const nextDisplayedFilters = { ...displayedFilters };

            nonAlwaysOnSources.forEach((source) => {
                nextDisplayedFilters[source] = true;
            });

            setFilters(filterValues, nextDisplayedFilters);
        }
    };

    return (
        <Button
            className="more-filters-button"
            color="primary"
            size="medium"
            startIcon={
                <FilterListIcon
                    sx={{
                        transform: isAllExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                />
            }
            sx={{
                alignSelf: 'flex-start',
                boxSizing: 'border-box',
                fontWeight: 600,
                height: 48,
                m: 0,
                marginBottom: '4px',
                textTransform: 'none',
            }}
            variant="outlined"
            onClick={handleToggle}
        >
            {isAllExpanded ? 'Less filters' : 'More filters'}
        </Button>
    );
};

MoreFiltersButton.propTypes = {
    filterInputs: PropTypes.node,
};

MoreFiltersButton.defaultProps = {
    filterInputs: null,
};

export default MoreFiltersButton;
